import React, { useEffect, useState } from 'react';
import { Link, useLocation } from '@reach/router';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedPackages, updateSelectedPackages } from 'reducers/misc';
import {
  Package,
  PackageGroup,
  PackagesList,
  Subjects,
} from 'pages/newSubscription/models';
import FreeTrialPackageCard from './FreeTrialPackageCard';
import { Spinner } from 'components/spinner/Spinner';
import { useFetchPackages } from 'hooks/useFetchPackages';
import { AppPath, PUBLIC_SITE_URL } from 'appConstants';
import { AddFreeTrialApi } from 'api/addFreeTrialApi';
import { useToast } from 'hooks/useToast';
import FormGeneralError from 'components/formComponents/FormGeneralError';

type ChoosePackageFormProps = {
  onSubmit: () => void;
};

export const ChooseFreeTrialPackageForm: React.FC<ChoosePackageFormProps> = ({
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const pathname = location.pathname;

  const selectedPackages = useSelector(selectSelectedPackages);

  const handleToast = useToast('Your free trial has been set up successfully.');

  const [allPackages, setAllPackages] = useState<PackagesList>([]);
  const [submitError, setSubmitError] = useState<string>('');

  const { packages, error } = useFetchPackages(pathname);

  useEffect(() => {
    if (!packages) return;
    setAllPackages(packages);
  }, [packages]);

  const selectPackage = (p: Package) => {
    const packageList = selectedPackages;
    const includesPackage = packageList.find(
      (i: Package) => i.packageID === p.packageID
    );

    if (includesPackage) {
      /*Clear unselected packages*/
      const filteredPackageList = packageList.filter(
        (i: Package) => i.packageID !== p.packageID
      );
      dispatch(updateSelectedPackages(filteredPackageList));
    } else {
      /*Clear packages of the same subject and different age group of the selected package*/
      const filteredPackageList = packageList.filter(
        (i: Package) => i.subject !== p.subject
      );

      dispatch(updateSelectedPackages([...filteredPackageList, p]));
    }
  };

  const isAgeGroupSelected = (ageGroup: string) => {
    /*To define the age group container styles*/
    const ageGroupPackage = selectedPackages.find(
      (i: Package) => i.ageGroup === ageGroup
    );
    return ageGroupPackage ? true : false;
  };

  const onContinue = async () => {
    if (selectedPackages.length === 0) return;

    const pathname = location.pathname;
    if (pathname === AppPath.NEW_FREE_TRIAL) {
      setSubmitError('');
      const selectedPackagesIds = selectedPackages.map(
        (p: Package) => p.packageID
      );

      const result = await AddFreeTrialApi.submitFreeTrialPackages(
        selectedPackagesIds
      );

      if (!result.error) {
        const scopes: string | null = localStorage.getItem('SCOPES');
        const parsedScopes = scopes && JSON.parse(scopes);
        const updatedScopes = [
          ...parsedScopes,
          {
            childElements: [],
            displayName: 'Upgrade Subscription',
            path: 'upgradesubscription',
          },
        ];
        localStorage.setItem('SCOPES', JSON.stringify(updatedScopes));
        handleToast();
        onSubmit();
      }
      if (result.error) {
        setSubmitError(result.error.message);
      }
    } else {
      onSubmit();
    }
  };

  if (allPackages.length === 0) {
    return (
      <div
        style={{
          minHeight: ' calc(100vh - 300px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <FormGeneralError message={submitError} />
      <div className="ChoosePackageForm">
        <div className="PackagesContainer">
          {allPackages.map((packageGroup: PackageGroup, i: number) => (
            <div
              key={i}
              className={`PackageOptions ${
                isAgeGroupSelected(packageGroup.name)
                  ? 'Selected'
                  : 'Unselected'
              }
              `}
            >
              <h2>{packageGroup.name}</h2>
              <p>{packageGroup.description + ' '}</p>
              {packageGroup &&
                packageGroup.subjects.map(
                  (subjectInfo: Subjects, index: number) => (
                    <FreeTrialPackageCard
                      key={index}
                      subjectInfo={subjectInfo}
                      selectPackage={selectPackage}
                      selectedPackages={selectedPackages}
                      ageGroup={packageGroup.name}
                    />
                  )
                )}
            </div>
          ))}
        </div>
        <div className="TotalDetails">
          <button
            className="Btn BtnOrange"
            type="submit"
            onClick={onContinue}
            disabled={selectedPackages.length === 0}
          >
            {AppPath.NEW_FREE_TRIAL ? 'Submit' : 'Continue'}
          </button>
        </div>
      </div>
    </>
  );
};
